import React, { useContext, useState } from 'react';
import { EventDetailsContext } from './context/event_details.context';
import LoadingPlaceholder from './components/loading_placeholder/loading_placeholder';
import Header from './components/header/header';
import { NAV_ENTRIES } from './constants/event_details.constants';
import FAQSection from './components/faq_section/faq_section';
import SpeakersSection from './components/speakers_section/speakers_section';
import PartnersSection from './components/partners_section/partners_section';
import FixedBottomCta from './components/fixed_bottom_cta/fixed_bottom_cta';
import styles from './container.module.scss';
import DateAndTime from './components/date_and_time/date_and_time';
import AlertPopup from '../components/react/alert_popup/alert_popup';

const Container: React.FC = () => {
  const { alertData, setAlertData, isLoading, event, activeTab, setActiveTab, speakers, sponsors } =
    useContext(EventDetailsContext);
  const [isAgendaCollapsed, setIsAgendaCollapsed] = useState<boolean>(false);

  const scrollToSection = (text: string): void => {
    setActiveTab(text);
    window.scrollTo({
      behavior: 'smooth',
      top: document.getElementById(text.toLowerCase()).offsetTop,
    });

    setTimeout(() => {
      setActiveTab('');
    }, 1500);
  };

  const navEntriesForPopulatedFields = NAV_ENTRIES.filter((entry) => {
    if (entry === 'Agenda') return event?.agenda && event?.agenda !== '<p></p>\n';
    if (entry === 'Speakers') return speakers?.length;
    if (entry === 'Partners') return sponsors?.length;
    return true;
  });

  const renderMenuSection: JSX.Element = (
    <div className={`py-3 sticky-top ${styles.menu}`} id='menu-section'>
      <div className={`container ${styles.limitedWidthForContainer}`}>
        <div className='row'>
          <div className='col-md-7 col'>
            <div className='row'>
              {navEntriesForPopulatedFields.map((text) => (
                <div className='col' key={text}>
                  <a
                    className={`py-3 ${styles.link} ${
                      activeTab === text
                        ? `text-orange-devex strong ${styles.borderUnderline}`
                        : 'text-gray-li'
                    }`}
                    onClick={() => scrollToSection(text)}
                  >
                    {text}
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderVirtualEventAdditionalInfo: JSX.Element = (
    <>
      <div className='mt-4'>
        <h3>How to watch</h3>
        <p>
          <span className='fw-bold'>Online: </span>Join the livestream via Zoom. A link to the
          livestream will be sent via email to all attendees.
        </p>
        <p>
          <span className='fw-bold'>On-Demand: </span>Unable to join at the time of streaming?
          Register anyway. A link to the recording of this event will be sent to everyone who is
          registered for this event.
        </p>
      </div>
      {!event?.multiDayEvent && (
        <div className='mt-4'>
          <h3>Live streaming time</h3>
          <p>
            <DateAndTime />
          </p>
        </div>
      )}
    </>
  );

  const renderAboutSection: JSX.Element = (
    <div
      id='about'
      // eslint-disable-next-line max-len
      className={`container py-5 ${styles.htmlField} ${styles.limitedWidthForContainer} ${styles.about}`}
    >
      <div className='row'>
        <div className='col'>
          <h1 className='emphasized fs-125'>About the event</h1>
          <div
            className='mt-4 border-bottom'
            data-testid='event-description'
            dangerouslySetInnerHTML={{ __html: event?.description }}
          />
          {event?.attendance_type === 'virtual' && renderVirtualEventAdditionalInfo}
          {event?.required_privilege ? (
            <p className='mt-3 fst-italic' data-testid='membership-info'>
              This is a FREE members-only event for{' '}
              <span className='fw-bold'>
                {event?.required_privilege.constant_name === 'CAN_ATTEND_CA_EVENT'
                  ? 'Career Account'
                  : 'Devex Pro'}{' '}
                subscribers.{' '}
              </span>
              Don’t have the right membership level? You can upgrade or sign up for a FREE TRIAL
              when registering for this event.
            </p>
          ) : (
            <p className='mt-3 fst-italic' data-testid='membership-info'>
              This is a FREE members-only event. You can create a FREE ACCOUNT when registering for
              this event.
            </p>
          )}
        </div>
      </div>
    </div>
  );

  const renderAgendaSection: JSX.Element = (
    <div id='agenda' className={`py-5 ${styles.htmlField} ${styles.agenda}`}>
      <div className={`container ${styles.limitedWidthForContainer}`}>
        <div className='row'>
          <div className='col'>
            <a
              data-bs-toggle='collapse'
              href='#collapsableAgenda'
              role='button'
              aria-expanded='true'
              aria-controls='collapsableAgenda'
              className='link-dark'
              onClick={() => setIsAgendaCollapsed(!isAgendaCollapsed)}
            >
              <h1
                // eslint-disable-next-line max-len
                className='emphasized mb-4 d-flex justify-content-between align-items-center fs-125'
              >
                Agenda
                <i
                  className={`fs-3 fal icon-angle-${isAgendaCollapsed ? 'down' : 'up'} float-end`}
                />
              </h1>
            </a>
            <div
              className={`px-35 py-4 ${styles.whiteContainer} collapse show`}
              id='collapsableAgenda'
              data-testid='event-agenda'
              dangerouslySetInnerHTML={{ __html: event?.agenda }}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderEventDetails: JSX.Element = (
    <>
      <AlertPopup alertData={alertData} setAlertData={setAlertData} />
      <Header />
      {renderMenuSection}
      {renderAboutSection}
      {event?.agenda && event.agenda !== '<p></p>\n' && renderAgendaSection}
      <SpeakersSection />
      <PartnersSection />
      <FAQSection />
      <div className='invisible' id='fixed-bottom-cta'>
        <FixedBottomCta />
      </div>
    </>
  );

  return isLoading ? <LoadingPlaceholder /> : renderEventDetails;
};

export default Container;
